import { useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AuthContext } from "~/utils/useAuth";
import { styled, keyframes } from "~/ui/style/stitches.config";
import {
  getAllDailySpreads,
  createDailySpread,
  updateDailySpread,
  deleteDailySpread,
  DailySpread,
} from "~/api/dailySpreadApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { ChevronDownIcon } from "@radix-ui/react-icons";

const fadeIn = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
});

const fadeOut = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 }
});

export const DailySpreadList = () => {
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [spreads, setSpreads] = useState<DailySpread[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [positions, setPositions] = useState<string[]>(["", ""]);
  const [scheduledDate, setScheduledDate] = useState("");

  // Bulk import state
  const [bulkText, setBulkText] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 2500);
  };

  // Check if user is authorized
  useEffect(() => {
    if (user && !user.isAdmin) {
      setLocation("/404");
    }
  }, [user, setLocation]);

  // Fetch spreads
  useEffect(() => {
    const fetchSpreads = async () => {
      if (!user || !user.isAdmin) return;
      try {
        const data = await getAllDailySpreads();
        setSpreads(data);
      } catch (error) {
        console.error("Failed to fetch spreads:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpreads();
  }, [user]);

  const resetForm = () => {
    setTitle("");
    setPositions(["", ""]);
    setScheduledDate("");
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const filteredPositions = positions.filter((p) => p.trim() !== "");
    if (!title.trim() || filteredPositions.length === 0) return;

    try {
      if (editingId) {
        const updated = await updateDailySpread(editingId, {
          title,
          positions: filteredPositions,
          scheduledDate: scheduledDate || null,
        });
        setSpreads(spreads.map((s) => (s.id === editingId ? updated : s)));
      } else {
        const created = await createDailySpread({
          title,
          positions: filteredPositions,
          scheduledDate: scheduledDate || null,
        });
        setSpreads([...spreads, created]);
        showToast("Spread added ✓");
      }
      resetForm();
    } catch (error) {
      console.error("Failed to save spread:", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this spread?")) return;
    try {
      await deleteDailySpread(id);
      setSpreads(spreads.filter((s) => s.id !== id));
    } catch (error) {
      console.error("Failed to delete spread:", error);
    }
  };

  const addPosition = () => setPositions([...positions, ""]);
  const removePosition = (index: number) => {
    if (positions.length > 1) {
      setPositions(positions.filter((_, i) => i !== index));
    }
  };
  const updatePosition = (index: number, value: string) => {
    const newPositions = [...positions];
    newPositions[index] = value;
    setPositions(newPositions);
  };

  // Inline edit handlers
  const startInlineEdit = (spread: DailySpread) => {
    setEditingId(spread.id);
    setTitle(spread.title);
    setPositions(spread.positions.length > 0 ? [...spread.positions] : ["", ""]);
    setScheduledDate(spread.scheduledDate?.split("T")[0] || "");
  };

  const cancelInlineEdit = () => {
    setEditingId(null);
    setTitle("");
    setPositions(["", ""]);
    setScheduledDate("");
  };

  const saveInlineEdit = async () => {
    if (!editingId) return;
    const filteredPositions = positions.filter((p) => p.trim() !== "");
    if (!title.trim() || filteredPositions.length === 0) return;

    try {
      const updated = await updateDailySpread(editingId, {
        title,
        positions: filteredPositions,
        scheduledDate: scheduledDate || null,
      });
      setSpreads(spreads.map((s) => (s.id === editingId ? updated : s)));
      cancelInlineEdit();
    } catch (error) {
      console.error("Failed to update spread:", error);
    }
  };

  const addInlinePosition = () => setPositions([...positions, ""]);
  const removeInlinePosition = (index: number) => {
    if (positions.length > 1) {
      setPositions(positions.filter((_, i) => i !== index));
    }
  };
  const updateInlinePosition = (index: number, value: string) => {
    const newPositions = [...positions];
    newPositions[index] = value;
    setPositions(newPositions);
  };

  // Render a spread item (either view or edit mode)
  const renderSpreadItem = (spread: DailySpread, showDate: boolean = true, isDuplicate: boolean = false) => {
    const isEditing = editingId === spread.id;

    if (isEditing) {
      return (
        <SpreadItemEdit key={spread.id}>
          <div className="edit-row">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              className="title-input"
            />
            <div
              className="date-wrapper"
              onClick={(e) => {
                const input = e.currentTarget.querySelector("input");
                if (input instanceof HTMLInputElement && typeof input.showPicker === "function") {
                  input.showPicker();
                }
              }}
            >
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </div>
          </div>
          <div className="positions-row">
            {positions.map((pos, i) => (
              <div key={i} className="position-input-row">
                <input
                  type="text"
                  value={pos}
                  onChange={(e) => updateInlinePosition(i, e.target.value)}
                  placeholder={`Position ${i + 1}`}
                />
                {positions.length > 1 && (
                  <button type="button" onClick={() => removeInlinePosition(i)}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="add-pos" onClick={addInlinePosition}>
              + position
            </button>
          </div>
          <div className="actions-row">
            <button className="cancel" onClick={cancelInlineEdit}>Cancel</button>
            <button className="save" onClick={saveInlineEdit}>Save</button>
          </div>
        </SpreadItemEdit>
      );
    }

    return (
      <SpreadItem key={spread.id} className={isDuplicate ? "duplicate" : ""}>
        <span className="title">{spread.title}</span>
        <span className="positions">
          {spread.positions.map((pos, i) => (
            <span key={i} className="position">{pos}:</span>
          ))}
        </span>
        <span className="date">
          {showDate && spread.scheduledDate
            ? (() => {
                const [year, month, day] = spread.scheduledDate.split("T")[0].split("-");
                return `${parseInt(month)}/${parseInt(day)}/${year}`;
              })()
            : ""}
          {isDuplicate && <span className="duplicate-dot" title="Duplicate date">•</span>}
        </span>
        <Actions>
          <button onClick={() => startInlineEdit(spread)}>edit</button>
          <button onClick={() => handleDelete(spread.id)}>×</button>
        </Actions>
      </SpreadItem>
    );
  };

  // Bulk import: parse "Title: Position 1, Position 2 - MM/DD/YYYY" format
  const handleBulkImport = async () => {
    const lines = bulkText.split("\n").filter((line) => line.trim() !== "");
    if (lines.length === 0) return;

    setBulkImporting(true);
    const newSpreads: DailySpread[] = [];

    for (const line of lines) {
      const colonIndex = line.indexOf(":");
      if (colonIndex === -1) continue;

      const spreadTitle = line.substring(0, colonIndex).trim();
      let rest = line.substring(colonIndex + 1).trim();

      // Check for date at the end (after " - ")
      let scheduledDate: string | null = null;
      const dateMatch = rest.match(/\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*$/);
      if (dateMatch) {
        const [month, day, year] = dateMatch[1].split("/");
        scheduledDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        rest = rest.substring(0, rest.lastIndexOf("-")).trim();
      }

      const spreadPositions = rest
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p !== "");

      if (!spreadTitle || spreadPositions.length === 0) continue;

      try {
        const created = await createDailySpread({
          title: spreadTitle,
          positions: spreadPositions,
          scheduledDate,
        });
        newSpreads.push(created);
      } catch (error) {
        console.error(`Failed to create spread "${spreadTitle}":`, error);
      }
    }

    setSpreads([...spreads, ...newSpreads]);
    setBulkText("");
    setBulkImporting(false);
    if (newSpreads.length > 0) {
      showToast(`${newSpreads.length} spread${newSpreads.length > 1 ? "s" : ""} imported ✓`);
    }
  };

  // Categorize spreads
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastSpreads = spreads
    .filter((s) => s.scheduledDate && new Date(s.scheduledDate) < today)
    .sort((a, b) => new Date(b.scheduledDate!).getTime() - new Date(a.scheduledDate!).getTime());
  const upcomingSpreads = spreads
    .filter((s) => s.scheduledDate && new Date(s.scheduledDate) >= today)
    .sort((a, b) => new Date(a.scheduledDate!).getTime() - new Date(b.scheduledDate!).getTime());
  const unscheduledSpreads = spreads.filter((s) => !s.scheduledDate);

  // Find duplicate dates
  const dateCounts = spreads.reduce((acc, s) => {
    if (s.scheduledDate) {
      const dateKey = s.scheduledDate.split("T")[0];
      acc[dateKey] = (acc[dateKey] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  const duplicateDates = new Set(
    Object.entries(dateCounts).filter(([, count]) => count > 1).map(([date]) => date)
  );

  if (!user || !user.isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <StyledPage>
        <PageContent>
          <SimpleLoadingIndicator />
        </PageContent>
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      {toastMessage && (
        <Toast>{toastMessage}</Toast>
      )}
      <PageContent>
        <div className="container">
          <h1>Schedule a Daily Spread</h1>

        <Form onSubmit={handleSubmit}>
            <FormRow>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
              />
            </FormRow>

            <FormRow>
              {positions.map((pos, i) => (
                <PositionRow key={i}>
                  <input
                    type="text"
                    value={pos}
                    onChange={(e) => updatePosition(i, e.target.value)}
                    placeholder={`Position ${i + 1}`}
                  />
                  {positions.length > 1 && (
                    <button type="button" onClick={() => removePosition(i)}>
                      ×
                    </button>
                  )}
                </PositionRow>
              ))}
              <AddButton type="button" onClick={addPosition}>
                + Add Position
              </AddButton>
            </FormRow>

            <FormRow>
              <label>Scheduled Date (optional)</label>
              <div
                className="date-wrapper"
                onClick={(e) => {
                  const input = e.currentTarget.querySelector("input");
                  if (input instanceof HTMLInputElement && typeof input.showPicker === "function") {
                    input.showPicker();
                  }
                }}
              >
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
            </FormRow>

            <ButtonRow>
              <SubmitButton type="submit">Create Spread</SubmitButton>
            </ButtonRow>
          </Form>

        <BulkImportSection>
          <h2>Bulk Import</h2>
          <p className="hint">One per line: Title: Position 1, Position 2 - MM/DD/YYYY (date optional)</p>
          <code className="example">people-pleasing audit: what I said yes to, what I actually have capacity for - 12/10/2025</code>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={6}
          />
          <SubmitButton
            type="button"
            onClick={handleBulkImport}
            disabled={bulkImporting || !bulkText.trim()}
          >
            {bulkImporting ? "Importing..." : "Import All"}
          </SubmitButton>
        </BulkImportSection>

        {upcomingSpreads.length > 0 && (
          <Section>
            <h2>Upcoming</h2>
            <SpreadList>
              {upcomingSpreads.map((spread) => renderSpreadItem(
                spread,
                true,
                spread.scheduledDate ? duplicateDates.has(spread.scheduledDate.split("T")[0]) : false
              ))}
            </SpreadList>
          </Section>
        )}

        {unscheduledSpreads.length > 0 && (
          <Section>
            <h2>Unscheduled Rotating Spreads</h2>
            <SpreadList>
              {unscheduledSpreads.map((spread) => renderSpreadItem(spread, false, false))}
            </SpreadList>
          </Section>
        )}

        {pastSpreads.length > 0 && (
          <Section>
            <CollapsibleHeader
              onClick={() => {
                const el = document.getElementById("past-spreads");
                const arrow = document.getElementById("past-spreads-arrow");
                if (el) {
                  const isHidden = el.style.display === "none";
                  el.style.display = isHidden ? "block" : "none";
                  if (arrow) arrow.style.transform = isHidden ? "rotate(-180deg)" : "rotate(0deg)";
                }
              }}
            >
              <h2>Past Spreads ({pastSpreads.length})</h2>
              <ChevronDownIcon id="past-spreads-arrow" style={{ transition: "transform 0.2s ease" }} />
            </CollapsibleHeader>
            <div id="past-spreads" style={{ display: "none" }}>
              <SpreadList>
                {pastSpreads.map((spread) => renderSpreadItem(
                  spread,
                  true,
                  spread.scheduledDate ? duplicateDates.has(spread.scheduledDate.split("T")[0]) : false
                ))}
              </SpreadList>
            </div>
          </Section>
        )}

          {spreads.length === 0 && (
            <EmptyState>No spreads yet. Create your first one above!</EmptyState>
          )}
        </div>
      </PageContent>
    </StyledPage>
  );
};

// Styled components
const StyledPage = styled("div", {
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "$wash",
  color: "#000",

  "& h1": {
    fontSize: "28px",
    fontFamily: "KeplerLightCondensed",
    marginBottom: "30px",
  },

  "& h2": {
    fontSize: "25px",
    fontFamily: "KeplerLightCondensed",
    fontWeight: "normal",
    marginBottom: "15px",
    opacity: 0.7,
  },
});

const PageContent = styled("div", {
  flex: 1,
  overflow: "auto",
  paddingTop: "80px",
  paddingBottom: "40px",

  "& .container": {
    maxWidth: "700px",
    margin: "0 auto",
    padding: "0 20px",
  },
});

const Toast = styled("div", {
  position: "fixed",
  top: "90px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#e7e7e7",
  color: "#000000",
  padding: "10px 16px",
  borderRadius: "20px",
  fontSize: "12px",
  zIndex: 1000,
  animation: `${fadeIn} 0.3s ease forwards, ${fadeOut} 0.3s ease forwards 2s`,
});

const Form = styled("form", {
  backgroundColor: "rgb(235, 235, 235)",
  border: "1px solid rgb(0, 0, 0, 0.4)",
  borderRadius: "20px",
  padding: "20px",
  marginBottom: "30px",
});

const FormRow = styled("div", {
  marginBottom: "15px",

  "& label": {
    display: "block",
    fontSize: "12px",
    marginBottom: "5px",
    opacity: 0.6,
  },

  "& input[type='text'], & input[type='date']": {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
  },

  "& .date-wrapper": {
    cursor: "pointer",

    "& input": {
      cursor: "pointer",
    },
  },
});

const PositionRow = styled("div", {
  display: "flex",
  gap: "8px",
  marginBottom: "8px",

  "& input": {
    flex: 1,
  },

  "& button": {
    width: "30px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    background: "none",
    cursor: "pointer",
    "&:hover": { background: "#f5f5f5" },
  },
});

const AddButton = styled("button", {
  fontSize: "12px",
  color: "#666",
  background: "none",
  border: "none",
  cursor: "pointer",
  "&:hover": { color: "#000" },
});

const ButtonRow = styled("div", {
  display: "flex",
  gap: "10px",
  marginTop: "20px",
});

const SubmitButton = styled("button", {
  padding: "10px 20px",
  backgroundColor: "#000",
  color: "#fff",
  border: "none",
  borderRadius: "11px",
  cursor: "pointer",
  "&:hover": { opacity: 0.8 },
});

const Section = styled("div", {
  marginBottom: "30px",
});

const BulkImportSection = styled("div", {
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: "20px",
  padding: "20px",
  marginBottom: "30px",

  "& h2": {
    marginBottom: "5px",
  },

  "& .hint": {
    fontSize: "12px",
    opacity: 0.6,
    marginBottom: "4px",
  },

  "& .example": {
    display: "block",
    fontSize: "12px",
    opacity: 0.5,
    marginBottom: "10px",
    fontFamily: "inherit",
    userSelect: "all",
    cursor: "text",
  },

  "& textarea": {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    marginBottom: "10px",
  },

  "& button:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

const CollapsibleHeader = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
  "&:hover": { opacity: 0.7 },
});

const SpreadList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const SpreadItem = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  padding: "12px 15px",
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: "14px",
  fontSize: "14px",
  flexWrap: "wrap",
  position: "relative",

  "& .title": {
    fontWeight: "500",
    fontSize: "12px",
    width: "180px",
    flexShrink: 0,

    "@mobile": {
      width: "100%",
      marginBottom: "4px",
    },
  },

  "& .positions": {
    width: "160px",
    fontSize: "13px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",

    "& .position": {
      display: "block",
    },

    "@mobile": {
      width: "100%",
    },
  },

  "& .date": {
    fontSize: "15px",
    fontWeight: "500",
    flex: 1,
    textAlign: "right",

    "& .duplicate-dot": {
      color: "#e57373",
      marginLeft: "4px",
    },

    "@mobile": {
      width: "100%",
      textAlign: "left",
      marginTop: "8px",
    },
  },

  "&.past": {
    opacity: 0.5,
  },
});

const SpreadItemEdit = styled("div", {
  padding: "15px",
  backgroundColor: "#fff",
  border: "1px solid #000",
  borderRadius: "8px",
  fontSize: "14px",

  "& .edit-row": {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",

    "@mobile": {
      flexDirection: "column",
    },
  },

  "& .title-input": {
    flex: 1,
    padding: "8px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
  },

  "& .date-wrapper": {
    position: "relative",
    cursor: "pointer",

    "& input": {
      width: "140px",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      fontSize: "14px",
      cursor: "pointer",

      "@mobile": {
        width: "100%",
      },
    },
  },

  "& .positions-row": {
    marginBottom: "10px",
  },

  "& .position-input-row": {
    display: "flex",
    gap: "8px",
    marginBottom: "6px",

    "& input": {
      flex: 1,
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      fontSize: "14px",
    },

    "& button": {
      width: "30px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      background: "none",
      cursor: "pointer",
      "&:hover": { background: "#f5f5f5" },
    },
  },

  "& .add-pos": {
    fontSize: "12px",
    color: "#666",
    background: "none",
    border: "none",
    cursor: "pointer",
    "&:hover": { color: "#000" },
  },

  "& .actions-row": {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",

    "& button": {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      cursor: "pointer",
    },

    "& .save": {
      backgroundColor: "#000",
      color: "#fff",
      border: "none",
      "&:hover": { opacity: 0.8 },
    },

    "& .cancel": {
      backgroundColor: "#fff",
      border: "1px solid #ddd",
      "&:hover": { background: "#f5f5f5" },
    },
  },
});

const Actions = styled("div", {
  display: "flex",
  gap: "8px",

  "@mobile": {
    position: "absolute",
    top: "auto",
    bottom: "12px",
    right: "15px",
  },

  "& button": {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "12px",
    opacity: 0.5,
    "&:hover": { opacity: 1 },
  },
});

const EmptyState = styled("div", {
  textAlign: "center",
  padding: "40px",
  opacity: 0.5,
});

